import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import moment from "moment";
import ConfirmationBox from "./ConfirmationBox.tsx";
import ServiceApiService from "../services/service.service";
import type { ServiceItem } from "../pages/models";

type ServiceDisplayStatus =
  | "scheduled"
  | "confirmed"
  | "paid"
  | "done"
  | "client_not_show_up"
  | "canceled_rob"
  | "canceled_client"
  | "canceled_overtime";

type ServiceDetailProps = {
  service: ServiceItem | null | undefined;
  show: boolean;
  onHide: () => void;
  onServiceChange: () => void;
  companyShortName?: string;
};

const statusBadge: Record<ServiceDisplayStatus, string> = {
  scheduled: "warning",
  confirmed: "danger",
  paid: "success",
  done: "info",
  client_not_show_up: "dark",
  canceled_rob: "secondary",
  canceled_client: "secondary",
  canceled_overtime: "secondary",
};

const getApiErrorMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null) {
    const apiError = error as {
      response?: {
        data?: {
          errors?: {
            nonFieldErrors?: string[];
            non_field_errors?: string[];
          } | string;
          message?: string;
        };
      };
      message?: string;
    };

    const errors = apiError.response?.data?.errors;
    const nonFieldErrors =
      typeof errors === "object" && errors !== null
        ? errors.nonFieldErrors?.[0] || errors.non_field_errors?.[0]
        : undefined;

    return (
      nonFieldErrors ||
      (typeof errors === "string"
        ? errors
        : undefined) ||
      apiError.response?.data?.message ||
      apiError.message ||
      "Unknown error"
    );
  }

  return "Unknown error";
};

const ServiceDetail = ({
  service,
  show,
  onHide,
  onServiceChange,
  companyShortName = "RDS",
}: ServiceDetailProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!service) return null;

  const formatDate = () => moment(service.startDateTime).format("LLL");
  const activeStatuses = ["scheduled", "confirmed", "paid"] as const;
  const depositAmount = Number(service.depositAmount ?? 0);
  const hasCollectedDeposit = depositAmount > 0 && service.status === "paid";
  const statusLabel: Record<ServiceDisplayStatus, string> = {
    scheduled: "Scheduled",
    confirmed: "Confirmed",
    paid: "Paid",
    done: "Done",
    client_not_show_up: "Client no-show",
    canceled_rob: `Canceled by ${companyShortName}`,
    canceled_client: "Canceled by Client",
    canceled_overtime: "Canceled (overtime)",
  };

  const doAction = async (action: () => Promise<unknown>) => {
    setIsSubmitting(true);
    try {
      await action();
      onServiceChange();
      onHide();
    } catch (error) {
      alert("Error: " + getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = async () => {
    const confirmed = await ConfirmationBox(
      `Mark service for ${service.client?.clientName} as done?`,
      "Yes",
      "No",
      { title: "Complete Service" }
    );
    if (confirmed) {
      doAction(() => ServiceApiService.completeService(service.id));
    }
  };

  const handleMissed = async () => {
    const confirmed = await ConfirmationBox(
      `Mark client ${service.client?.clientName} as no-show?`,
      "Yes",
      "No",
      { title: "Client No-Show" }
    );
    if (confirmed) {
      doAction(() => ServiceApiService.missedService(service.id));
    }
  };

  const handleCancel = async (withRefund: boolean) => {
    const confirmed = await ConfirmationBox(
      `Cancel service for ${service.client?.clientName} at ${formatDate()}${
        withRefund ? " WITH refund" : " WITHOUT refund"
      }?`,
      "Yes",
      "No",
      { title: "Cancel Service" }
    );
    if (confirmed) {
      doAction(() =>
        ServiceApiService.cancelService(service.id, {
          withRefund: withRefund ? "true" : "false",
        })
      );
    }
  };

  const handleDeposit = async () => {
    if (!service.client) {
      alert(
        "This service has no assigned client. Assign a client first, then confirm deposit."
      );
      return;
    }

    const confirmed = await ConfirmationBox(
      `Confirm cash deposit for ${service.client?.clientName}?`,
      "Yes",
      "No",
      { title: "Confirm Deposit" }
    );
    if (confirmed) {
      doAction(() => ServiceApiService.depositService(service.id));
    }
  };

  const isPaid = service.status === "paid";
  const isActiveStatus = activeStatuses.includes(
    service.status as (typeof activeStatuses)[number],
  );
  const showRefundWithout =
    hasCollectedDeposit &&
    (() => {
      const diff = Math.floor(
        (new Date(service.startDateTime).getTime() - Date.now()) /
          (1000 * 60 * 60 * 48),
      );
      return diff < 1;
    })();

  const statusKey = service.status as ServiceDisplayStatus;
  const phoneNumber = service.client?.phoneNumber || service.client?.phone_number;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>
          Service &mdash; {formatDate()}{" "}
          <Badge bg={statusBadge[statusKey] || "secondary"}>
            {statusLabel[statusKey] || service.status}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-2 mb-3">
          <div className="col-6">
            <strong>Client:</strong> {service.client?.clientName || "—"}
          </div>
          <div className="col-6">
            <strong>Phone:</strong>{" "}
            {phoneNumber ? <a href={`tel:${phoneNumber}`}>{phoneNumber}</a> : "-"}
          </div>
          <div className="col-6">
            <strong>Duration:</strong> {service.durationHours} hour
            {service.durationHours > 1 ? "s" : ""}
          </div>
          <div className="col-6">
            <strong>Technician:</strong> {service.technician?.name || "—"}
          </div>
          <div className="col-6">
            <strong>Motorcycle:</strong>{" "}
            {[service.year, service.mark, service.model]
              .filter(Boolean)
              .join(", ") || "—"}
          </div>
          <div className="col-6">
            <strong>Deposit:</strong>{" "}
            {service.depositAmount ? `$${service.depositAmount}` : "—"}
          </div>
          {service.note && (
            <div className="col-12">
              <strong>Notes:</strong> {service.note}
            </div>
          )}
        </div>

        <div className="d-flex flex-wrap gap-2">
          {isActiveStatus && (
            <>
              <Button
                variant="danger"
                onClick={() => handleCancel(hasCollectedDeposit)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  hasCollectedDeposit ? "Cancel (with refund)" : "Cancel"
                )}
              </Button>
              {showRefundWithout && (
                <Button
                  variant="outline-danger"
                  onClick={() => handleCancel(false)}
                  disabled={isSubmitting}
                >
                  Cancel (no refund)
                </Button>
              )}
            </>
          )}
          {isPaid && (
            <>
              <Button
                variant="success"
                onClick={handleComplete}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "Complete"
                )}
              </Button>
              <Button
                variant="dark"
                onClick={handleMissed}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "No-Show"
                )}
              </Button>
            </>
          )}
          {service.status === "scheduled" && (
            <Button
              variant="primary"
              onClick={handleDeposit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Spinner size="sm" animation="border" />
              ) : (
                "Confirm Cash Deposit"
              )}
            </Button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ServiceDetail;
